import { useState } from "react";

import { formatScheduleDate } from "../../domain/schedule";
import { Button } from "../../shared/ui/button";
import { ConfirmationDialog } from "../../shared/ui/confirmation-dialog";
import { PageHeader } from "../../shared/ui/page-header";
import { GameSetupStep } from "./GameSetupStep";
import { LineupSetupStep } from "./LineupSetupStep";
import { RosterSetupStep } from "./RosterSetupStep";
import { SetupPrimaryAction } from "./SetupPrimaryAction";
import { SetupWeatherCard } from "./SetupWeatherCard";
import type { SetupScreenProps } from "./setupTypes";
import { actionKeys } from "../../hooks/actionKeys";
import { EMPTY_INTENT_STATUS } from "../../hooks/useIntentStatus";

export function SetupScreen({
  toast,
  hasActiveGame,
  manageOpen,
  managementPanel,
  players,
  activePlayerIds,
  lineupSlots,
  lineupMode = "pregame",
  preferences,
  expandedPlayerId,
  confirmDeletePlayerId,
  jerseyHolderPlayerId,
  opponentName,
  opponentLocked = false,
  isHome,
  authoritativeRevision,
  scheduledDate,
  timeSlot,
  field,
  beerDuty,
  battingCount,
  serverBusy,
  actionStatus = EMPTY_INTENT_STATUS,
  weatherState,
  onShowGame,
  onToggleManage,
  onSetAttendance,
  onTogglePlayerExpanded,
  onTogglePreference,
  onSetJerseyHolder,
  onRequestPlayerRemoval,
  onConfirmPlayerRemoval,
  onCancelPlayerRemoval,
  onSuggestLineup,
  onCommitLineup,
  onSetOpponentName,
  onSetHomeTeam,
  onSetScheduleDetails,
  onShowDefense,
  onStartGame,
  onRetryWeather = () => undefined,
  weatherRefreshing = false,
}: SetupScreenProps) {
  const [futureStartConfirmationOpen, setFutureStartConfirmationOpen] =
    useState(false);
  const removalPlayer =
    players.find((player) => player.id === confirmDeletePlayerId) ?? null;
  const pregameEditable = lineupMode === "pregame";
  const liveEditable = lineupMode === "live";
  const emptySlots = pregameEditable
    ? Math.max(activePlayerIds.length - battingCount, 0)
    : 0;
  const scheduleComplete =
    scheduledDate === undefined
      ? true
      : Boolean(
          opponentName.trim() && scheduledDate && timeSlot && field && beerDuty,
        );
  const requestStartGame = () => {
    if (
      scheduledDate &&
      scheduledDate > new Date().toISOString().slice(0, 10)
    ) {
      setFutureStartConfirmationOpen(true);
      return;
    }
    void onStartGame();
  };
  const startPending = actionStatus.isPending(actionKeys.startGame);
  const startUnknown =
    actionStatus.phaseFor(actionKeys.startGame) === "outcome_unknown";
  const removalPending = removalPlayer
    ? actionStatus.isPending(actionKeys.removePlayer(removalPlayer.id))
    : false;
  const removalUnknown = removalPlayer
    ? actionStatus.phaseFor(actionKeys.removePlayer(removalPlayer.id)) ===
      "outcome_unknown"
    : false;

  return (
    <div className="mx-auto grid w-full min-w-0 max-w-[var(--content-narrow)] gap-3 [&>*]:min-w-0">
      {toast ? (
        <div
          role="status"
          className="sticky top-[calc(3.75rem+var(--safe-top))] z-[var(--z-sticky)] rounded-[var(--radius-sm)] border border-warning/60 bg-warning px-3 py-2 text-center text-sm font-bold text-warning-foreground shadow-md"
        >
          {toast}
        </div>
      ) : null}

      <PageHeader
        title="Game setup"
        description={
          liveEditable
            ? "Update the batting order, opponent, and defense for the active game."
            : lineupMode === "locked"
              ? "This completed game's setup is locked."
              : "Set attendance, batting order, and tonight's matchup."
        }
        actions={
          <>
            {hasActiveGame ? (
              <Button
                type="button"
                onClick={onShowGame}
                size="sm"
                variant="outline"
              >
                ← GAME
              </Button>
            ) : null}
            <Button
              type="button"
              onClick={onToggleManage}
              size="sm"
              variant={manageOpen ? "selected" : "outline"}
            >
              ⚙ MANAGE
            </Button>
          </>
        }
      />

      {managementPanel}

      <RosterSetupStep
        players={players}
        activePlayerIds={activePlayerIds}
        preferences={preferences}
        expandedPlayerId={expandedPlayerId}
        jerseyHolderPlayerId={jerseyHolderPlayerId}
        editable={pregameEditable}
        onSetAttendance={onSetAttendance}
        onTogglePlayerExpanded={onTogglePlayerExpanded}
        onTogglePreference={onTogglePreference}
        onSetJerseyHolder={onSetJerseyHolder}
        onRequestPlayerRemoval={onRequestPlayerRemoval}
        actionStatus={actionStatus}
      />

      <LineupSetupStep
        players={players}
        activePlayerIds={activePlayerIds}
        lineupSlots={lineupSlots}
        lineupMode={lineupMode}
        battingCount={battingCount}
        onSuggestLineup={onSuggestLineup}
        onCommitLineup={onCommitLineup}
        onSetAttendance={onSetAttendance}
        actionStatus={actionStatus}
      />

      <GameSetupStep
        players={players}
        lineupMode={lineupMode}
        opponentName={opponentName}
        opponentLocked={opponentLocked}
        isHome={isHome}
        authoritativeRevision={authoritativeRevision}
        scheduledDate={scheduledDate}
        timeSlot={timeSlot}
        field={field}
        beerDuty={beerDuty}
        onSetOpponentName={onSetOpponentName}
        onSetHomeTeam={onSetHomeTeam}
        onSetScheduleDetails={onSetScheduleDetails}
        onShowDefense={onShowDefense}
        actionStatus={actionStatus}
      />

      <SetupWeatherCard
        state={weatherState}
        onRetry={onRetryWeather}
        refreshing={weatherRefreshing}
      />

      <SetupPrimaryAction
        hasActiveGame={hasActiveGame}
        liveEditable={liveEditable}
        battingCount={battingCount}
        emptySlots={emptySlots}
        scheduleComplete={scheduleComplete}
        serverBusy={serverBusy}
        startPending={startPending}
        startUnknown={startUnknown}
        onShowGame={onShowGame}
        onStartGame={requestStartGame}
      />

      <ConfirmationDialog
        open={futureStartConfirmationOpen}
        title="Start this future game now?"
        description={`${opponentName} is scheduled for ${formatScheduleDate(scheduledDate)}. Starting now makes the game live for the whole team.`}
        confirmLabel="START GAME NOW"
        cancelLabel="NOT YET"
        pending={startPending}
        pendingLabel="STARTING GAME…"
        unknown={startUnknown}
        onRetryUnknown={actionStatus.retryUnknown}
        destructive={false}
        onConfirm={async () => {
          if (await onStartGame()) setFutureStartConfirmationOpen(false);
        }}
        onOpenChange={setFutureStartConfirmationOpen}
      />

      <ConfirmationDialog
        open={removalPlayer !== null}
        title={
          removalPlayer
            ? `Remove ${removalPlayer.name} from the roster?`
            : "Remove player?"
        }
        description="This removes the player from future lineups. Existing archived game statistics remain unchanged."
        confirmLabel="REMOVE"
        cancelLabel="KEEP"
        pending={removalPending}
        pendingLabel="REMOVING…"
        unknown={removalUnknown}
        onRetryUnknown={actionStatus.retryUnknown}
        onConfirm={() => {
          if (removalPlayer) void onConfirmPlayerRemoval(removalPlayer.id);
        }}
        onOpenChange={(open) => {
          if (!open) onCancelPlayerRemoval();
        }}
      />
    </div>
  );
}
